import { useQuery } from '@tanstack/react-query'
import { ClientFinancingProfileResponse } from '@/core/api/apiClient'
import { apiClient } from '@/core/api/apiClient'
import { useAuth } from 'hooks/auth/useAuth'
import { useState, useMemo } from 'react'

interface GetAllFinancingProfilesResponse {
  success: boolean;
  data: ClientFinancingProfileResponse[];
  total: number;
  page?: number;
  limit?: number;
  hasMore?: boolean;
  lastDocId?: string | null;
  allProfiles?: boolean;
}

export const useClientFinancingProfiles = () => {
  const { userInfo } = useAuth()

  const {
    data: clientFinancingProfilesData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'clientFinancingProfiles'],
    queryFn: async (): Promise<ClientFinancingProfileResponse[]> => {
      if (!['admin', 'babyAdmin'].includes(userInfo?.role || '')) {
        throw new Error('Unauthorized: User does not have admin permissions')
      }

      const response = await apiClient.portal.admin.getAllClientFinancingProfiles()  
      return Array.isArray(response.data) ? response.data : []
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: Boolean(['admin', 'babyAdmin'].includes(userInfo?.role || '')),
  })

  const clientFinancingProfiles = Array.isArray(clientFinancingProfilesData) 
    ? clientFinancingProfilesData 
    : []

  return {
    clientFinancingProfiles,
    isLoading,
    isError,
    error,
    refetchClientFinancingProfiles: refetch,
  }
}

export const useManageClientFinancingProfiles = () => {
  const { userInfo } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [profileCompletedFilter, setProfileCompletedFilter] = useState<'all' | 'true' | 'false'>('all')

  const {
    data: allProfilesResponse,
    isLoading: isLoadingProfiles,
    isError: profilesError,
    error: profilesErrorDetails,
    refetch: refetchProfiles,
  } = useQuery({
    queryKey: ['admin', 'allFinancingProfiles'],
    queryFn: async (): Promise<GetAllFinancingProfilesResponse> => {
      if (!['admin', 'babyAdmin'].includes(userInfo?.role || '')) {
        throw new Error('Unauthorized: User does not have admin permissions')
      }

      const response = await apiClient.portal.admin.getAllClientFinancingProfiles()
      return response
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: Boolean(['admin', 'babyAdmin'].includes(userInfo?.role || '')),
  })

  const allProfiles = allProfilesResponse?.data || []

  const isProfileCompleted = (profile: ClientFinancingProfileResponse) => {
    if (!profile.financingProfile) return false

    const fp = profile.financingProfile
    const hasBusinessInfo = fp.business_name && fp.entity_structure
    const hasFinancialInfo = fp.last_year_revenue !== undefined && fp.last_year_expenses !== undefined
    const hasPersonalInfo = fp.personal_info?.first_name && fp.personal_info?.last_name

    return hasBusinessInfo && hasFinancialInfo && hasPersonalInfo
  }

  const getSearchableText = (profile: ClientFinancingProfileResponse) => {
    const searchableFields = [
      profile.email,
      profile.first_name,
      profile.last_name,
      profile.company_name,
      profile.dealership_name,
      profile.financingProfile?.business_name,
    ].filter(Boolean)
    
    return searchableFields.join(' ').toLowerCase()
  }

  const filteredProfiles = useMemo(() => {
    if (!allProfiles.length) return []

    let filtered = allProfiles

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(profile => {
        const searchableText = getSearchableText(profile)
        return searchableText.includes(searchLower)
      })
    }

    if (profileCompletedFilter !== 'all') {
      filtered = filtered.filter(profile => {
        const completed = isProfileCompleted(profile)
        return profileCompletedFilter === 'true' ? completed : !completed
      })
    }

    return filtered
  }, [allProfiles, searchTerm, profileCompletedFilter])

  return {
    allFinancingProfiles: allProfiles,
    totalProfiles: allProfiles.length,
    financingProfiles: filteredProfiles,
    filteredCount: filteredProfiles.length,
    isLoading: isLoadingProfiles,
    isError: profilesError,
    error: profilesErrorDetails,
    refetchFinancingProfiles: refetchProfiles,
    searchTerm,
    setSearchTerm,
    profileCompletedFilter,
    setProfileCompletedFilter,
    isProfileCompleted,
  }
}

export const useClientFinancingProfilesById = (userIds: string[], enabled: boolean = true) => {
  const { userInfo } = useAuth()

  const {
    data: clientFinancingProfilesData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'clientFinancingProfilesById', userIds],
    queryFn: async (): Promise<ClientFinancingProfileResponse[]> => {
      if (!['admin', 'babyAdmin'].includes(userInfo?.role || '')) {
        throw new Error('Unauthorized: User does not have admin permissions')
      }

      if (!userIds || userIds.length === 0) {
        return []
      }

      const response = await apiClient.portal.admin.getClientFinancingProfilesById(userIds)
      return Array.isArray(response) ? response : []
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: Boolean(
      ['admin', 'babyAdmin'].includes(userInfo?.role || '') && 
      enabled && 
      userIds && 
      userIds.length > 0
    ),
  })

  const clientFinancingProfiles = Array.isArray(clientFinancingProfilesData) 
    ? clientFinancingProfilesData 
    : []

  return {
    clientFinancingProfiles,
    isLoading,
    isError,
    error,
    refetchClientFinancingProfiles: refetch,
  }
} 